import { useLayoutEffect, useMemo, useRef } from "react";
import { CanvasTexture, InstancedMesh, Object3D, SRGBColorSpace } from "three";
import { MEETING_POINT, DELIVERY_ZONE, BRAND_GREEN, SAFETY_STRIP } from "./layout";
import { SignDecal } from "./SignDecal";

/**
 * Extintor escénico: cilindro rojo de 0.5 m con manguera y boquilla
 * [NORMATIVO Dec. 1072/2015, SG-SST]. Se monta en muro o piso.
 */
export function FireExtinguisher({
  position,
  rotationY = 0,
}: {
  position: [number, number, number];
  rotationY?: number;
}) {
  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      {/* cuerpo */}
      <mesh position={[0, 0.25, 0]} castShadow>
        <cylinderGeometry args={[0.07, 0.07, 0.4, 12]} />
        <meshStandardMaterial color="#d5342c" roughness={0.35} metalness={0.25} />
      </mesh>
      {/* válvula y manija */}
      <mesh position={[0, 0.48, 0]}>
        <cylinderGeometry args={[0.02, 0.02, 0.06, 8]} />
        <meshStandardMaterial color="#3a3f42" metalness={0.6} roughness={0.4} />
      </mesh>
      <mesh position={[0.04, 0.5, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.012, 0.012, 0.1, 6]} />
        <meshStandardMaterial color="#3a3f42" metalness={0.6} roughness={0.4} />
      </mesh>
      {/* manguera */}
      <mesh position={[0.07, 0.35, 0]} rotation={[0, 0, 0.5]}>
        <cylinderGeometry args={[0.012, 0.012, 0.3, 6]} />
        <meshStandardMaterial color="#1c1f21" roughness={0.9} />
      </mesh>
      {/* etiqueta */}
      <mesh position={[0, 0.3, 0.072]}>
        <planeGeometry args={[0.1, 0.14]} />
        <meshStandardMaterial color="#f0efe8" roughness={0.7} />
      </mesh>
    </group>
  );
}

/** Botiquín de primeros auxilios: caja blanca con cruz verde [Dec. 1072/2015]. */
export function FirstAidBox({
  position,
  rotationY = 0,
}: {
  position: [number, number, number];
  rotationY?: number;
}) {
  const cross = useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#f4f4f0";
    ctx.fillRect(0, 0, 64, 64);
    ctx.fillStyle = "#0d7a3f";
    ctx.fillRect(24, 10, 16, 44);
    ctx.fillRect(10, 24, 44, 16);
    const tex = new CanvasTexture(canvas);
    tex.colorSpace = SRGBColorSpace;
    return tex;
  }, []);
  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      <mesh castShadow>
        <boxGeometry args={[0.34, 0.26, 0.14]} />
        <meshStandardMaterial color="#f4f4f0" roughness={0.5} />
      </mesh>
      <mesh position={[0, 0, 0.075]}>
        <planeGeometry args={[0.2, 0.2]} />
        <meshStandardMaterial map={cross} roughness={0.6} />
      </mesh>
    </group>
  );
}

/**
 * Punto de encuentro al final de la ruta de egreso norte
 * [NORMATIVO Dec. 1072/2015]: demarcación circular y placa verde.
 */
export function MeetingPoint() {
  const { x, z, radius } = MEETING_POINT;
  return (
    <group name="punto-encuentro" position={[x, 0, z]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
        <circleGeometry args={[radius, 32]} />
        <meshStandardMaterial color="#0d7a3f" roughness={0.9} />
      </mesh>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0]}>
        <ringGeometry args={[radius - 0.4, radius - 0.15, 32]} />
        <meshStandardMaterial color="#f0efe8" roughness={0.85} />
      </mesh>
      <mesh position={[0, 1.1, -radius + 0.3]} castShadow>
        <cylinderGeometry args={[0.04, 0.04, 2.2, 8]} />
        <meshStandardMaterial color="#7d8488" metalness={0.6} roughness={0.4} />
      </mesh>
      <SignDecal text="PUNTO DE ENCUENTRO" position={[0, 2, -radius + 0.3]} variant="evac" width={2.4} height={0.5} />
    </group>
  );
}

/**
 * Barreras plásticas enlazadas (módulos de 1 m, rojo/blanco alternados)
 * bordeando las franjas de seguridad de una pista
 * [NORMATIVO Anexo 60, num. 2.3.1 Nota 6].
 */
export function PlasticBarriers({ x, z, w, d }: { x: number; z: number; w: number; d: number }) {
  const redRef = useRef<InstancedMesh>(null);
  const whiteRef = useRef<InstancedMesh>(null);

  const { red, white } = useMemo(() => {
    const inset = SAFETY_STRIP / 2 + 0.15;
    const pts: { x: number; z: number; rotY: number }[] = [];
    const nx = Math.floor(w / 1);
    const nz = Math.floor(d / 1);
    for (let i = 0; i < nx; i++) {
      const px = x - w / 2 + 0.5 + i * 1;
      pts.push({ x: px, z: z - d / 2 + inset, rotY: 0 });
      pts.push({ x: px, z: z + d / 2 - inset, rotY: 0 });
    }
    for (let i = 0; i < nz; i++) {
      const pz = z - d / 2 + 0.5 + i * 1;
      pts.push({ x: x - w / 2 + inset, z: pz, rotY: Math.PI / 2 });
      pts.push({ x: x + w / 2 - inset, z: pz, rotY: Math.PI / 2 });
    }
    const red: import("three").Matrix4[] = [];
    const white: import("three").Matrix4[] = [];
    const dummy = new Object3D();
    pts.forEach((p, i) => {
      dummy.position.set(p.x, 0.25, p.z);
      dummy.rotation.set(0, p.rotY, 0);
      dummy.updateMatrix();
      (i % 2 === 0 ? red : white).push(dummy.matrix.clone());
    });
    return { red, white };
  }, [x, z, w, d]);

  useLayoutEffect(() => {
    if (redRef.current) {
      red.forEach((m, i) => redRef.current!.setMatrixAt(i, m));
      redRef.current.instanceMatrix.needsUpdate = true;
    }
    if (whiteRef.current) {
      white.forEach((m, i) => whiteRef.current!.setMatrixAt(i, m));
      whiteRef.current.instanceMatrix.needsUpdate = true;
    }
  }, [red, white]);

  return (
    <group>
      <instancedMesh ref={redRef} args={[undefined, undefined, red.length]} castShadow>
        <boxGeometry args={[0.95, 0.5, 0.28]} />
        <meshStandardMaterial color="#c03a2b" roughness={0.55} />
      </instancedMesh>
      <instancedMesh ref={whiteRef} args={[undefined, undefined, white.length]} castShadow>
        <boxGeometry args={[0.95, 0.5, 0.28]} />
        <meshStandardMaterial color="#f0efe8" roughness={0.55} />
      </instancedMesh>
    </group>
  );
}

/**
 * Zona de entrega del vehículo junto al punto de inicio de la primera
 * maniobra [NORMATIVO Anexo 60, num. 2.3.1 Nota 8]: demarcación pintada.
 */
export function DeliveryZone() {
  const { x, z, w, d } = DELIVERY_ZONE;
  return (
    <group name="zona-entrega">
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[x, 0.03, z]}>
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial color={BRAND_GREEN} roughness={0.9} />
      </mesh>
      {/* marco blanco punteado visual */}
      {[
        [0, d / 2, w, 0.12],
        [0, -d / 2, w, 0.12],
        [w / 2, 0, 0.12, d],
        [-w / 2, 0, 0.12, d],
      ].map(([ox, oz, sw, sd], i) => (
        <mesh key={i} rotation={[-Math.PI / 2, 0, 0]} position={[x + ox, 0.04, z + oz]}>
          <planeGeometry args={[sw, sd]} />
          <meshStandardMaterial color="#f0efe8" roughness={0.8} />
        </mesh>
      ))}
      <SignDecal text="ENTREGA DE VEHÍCULO" position={[x, 0.05, z]} orientation="floor" variant="evac" width={3.2} height={0.5} />
    </group>
  );
}
